import { BuyerType } from "@/framework/billing/stripe";
import { euCountryCodes } from "@/framework/billing/countries/euCountryCodes";

// Gross amount from which the invoicing API requires customer name + address.
export const smallAmountInvoiceLimitEur = 250;

type TopupTaxPolicyParams = {
  buyerType: BuyerType;
  countryCode: string;
  amountEur: number;
};

export class TopupTaxPolicy {
  constructor(private readonly params: TopupTaxPolicyParams) {}

  get requiresVatId(): boolean {
    return this.params.buyerType === "business";
  }

  // Small-amount invoices (Kleinbetragsrechnung, gross < 250 €) need no
  // customer name / address, so private buyers below that limit skip it.
  // Businesses always need it for input-tax deduction and reverse charge.
  get requiresBillingAddress(): boolean {
    return (
      this.params.buyerType === "business" ||
      this.grossAmount >= smallAmountInvoiceLimitEur
    );
  }

  // German buyers always pay German VAT. EU private buyers do too: EU-wide B2C
  // sales stay below the €10k/year OSS threshold, so German VAT applies
  // instead of the buyer country's rate.
  get chargesVat(): boolean {
    return (
      this.params.countryCode === "DE" ||
      (this.params.buyerType === "private" &&
        euCountryCodes.has(this.params.countryCode))
    );
  }

  get vatRate(): number {
    return this.chargesVat ? 0.19 : 0;
  }

  get netAmount(): number {
    return this.params.amountEur;
  }

  // Returns VAT amount rounded to cents for display.
  get vatAmount(): number {
    return Math.round(this.params.amountEur * this.vatRate * 100) / 100;
  }

  // Returns gross amount (net + VAT) rounded to cents for display.
  get grossAmount(): number {
    return this.params.amountEur + this.vatAmount;
  }

  get isReverseCharge(): boolean {
    return (
      this.params.buyerType === "business" &&
      this.params.countryCode !== "" &&
      !this.chargesVat
    );
  }
}
